import { api } from '../../app/api';

import type {
    Attachment,
    CommentForm,
    Dashboard,
    OrderSearchItem,
    Ticket,
    TicketDetail,
    TicketEvent,
    TicketForm,
    TicketLabel,
    TicketRecurrence,
    TicketReference,
    TicketReferenceType,
    TicketStatus,
} from './tickets.schema';
import {
    ArchiveAllResponseSchema,
    AttachmentListResponseSchema,
    AttachmentSingleResponseSchema,
    DashboardResponseSchema,
    LabelColorMapResponseSchema,
    LabelColorResponseSchema,
    LabelSuggestResponseSchema,
    OrderSearchResponseSchema,
    TicketDetailResponseSchema,
    TicketEventListResponseSchema,
    TicketEventSingleResponseSchema,
    TicketLabelResponseSchema,
    TicketListResponseSchema,
    TicketRecurrenceListResponseSchema,
    TicketRecurrenceResponseSchema,
    TicketReferenceListResponseSchema,
    TicketReferenceSingleResponseSchema,
    TicketResponseSchema,
} from './tickets.schema';

type ListArgs = {
    start?: number;
    limit?: number;
    search?: string;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    // One status or a set (serialized comma-separated; the API maps a set to IN).
    status?: TicketStatus | TicketStatus[];
    assigneeUserId?: number;
    // Only tickets with no assignee (mutually exclusive with assigneeUserId,
    // which wins when both are set — mirrors the API).
    unassigned?: boolean;
    // "My tickets" — shop-shared: my own tickets plus everyone at my shop. The
    // API resolves the set from the session user; we just send the flag. Wins
    // over assigneeUserId/unassigned server-side.
    mine?: boolean;
    overdue?: boolean;
    // The archive view passes archived=true; the active board omits it.
    archived?: boolean;
    // The backlog view passes backlog=true; the active board omits it.
    backlog?: boolean;
    // Reverse lookup: only tickets referencing this object. Without an explicit
    // shelf param the API returns the object's FULL history (all shelves).
    refType?: TicketReferenceType;
    refId?: string | number;
    // Inline each non-done ticket's comment thread (entity preview listings).
    withComments?: boolean;
};

function serializeForm(values: TicketForm) {
    return {
        title: values.title,
        description: values.description || null,
        priority: values.priority,
        dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
        assigneeUserId: values.assigneeUserId ?? null,
        assigneeName: values.assigneeUserId != null ? values.assigneeName ?? null : null,
        labels: values.labels ?? [],
    };
}

// Monotonic negative ids for optimistic comment / label rows so they never
// collide with real server ids and read as obvious placeholders until the
// invalidation refetch swaps in the real row.
let optimisticSeq = 0;
const nextOptimisticId = () => --optimisticSeq;

const ticketsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listTickets: builder.query<{ data: Ticket[]; count: number }, ListArgs | void>({
            query: (args) => {
                const a = args ?? {};
                // Default to the manual drag order (position ASC); the API sinks
                // done tickets below the active ones regardless of this sort.
                const { start = 0, limit = 20, search, sort = 'position', direction = 'ASC' } = a;
                return {
                    url: '/v2/tickets',
                    method: 'GET',
                    params: {
                        _start: start,
                        _end: start + limit,
                        _sort: sort,
                        _order: direction,
                        q: search || undefined,
                        status: Array.isArray(a.status)
                            ? (a.status.length ? a.status.join(',') : undefined)
                            : a.status || undefined,
                        mine: a.mine ? true : undefined,
                        assigneeUserId: a.assigneeUserId || (a.unassigned ? 'none' : undefined),
                        overdue: a.overdue ? true : undefined,
                        archived: a.archived ? true : undefined,
                        backlog: a.backlog ? true : undefined,
                        refType: a.refType || undefined,
                        refId: a.refId != null ? String(a.refId) : undefined,
                        withComments: a.withComments ? true : undefined,
                    },
                };
            },
            transformResponse: (raw: unknown) => {
                const parsed = TicketListResponseSchema.parse(raw);
                return { data: parsed.data, count: parsed.count };
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map((t) => ({ type: 'Ticket' as const, id: t.id })),
                          { type: 'Ticket' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Ticket' as const, id: 'LIST' }],
        }),

        getTicket: builder.query<TicketDetail, number>({
            query: (id) => ({ url: '/v2/tickets/[id]', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => TicketDetailResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'Ticket', id }],
        }),

        createTicket: builder.mutation<Ticket, TicketForm>({
            query: (values) => ({ url: '/v2/tickets', method: 'POST', body: serializeForm(values) }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'Ticket', id: 'LIST' }],
        }),

        updateTicket: builder.mutation<Ticket, { id: number; values: TicketForm }>({
            query: ({ id, values }) => ({
                url: '/v2/tickets/[id]',
                method: 'PUT',
                query: { id },
                body: serializeForm(values),
            }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
                { type: 'TicketEvent', id },
            ],
        }),

        setTicketStatus: builder.mutation<Ticket, { id: number; status: TicketStatus }>({
            query: ({ id, status }) => ({
                url: '/v2/tickets/[id]/status',
                method: 'POST',
                query: { id },
                body: { status },
            }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
                { type: 'TicketEvent', id },
            ],
            async onQueryStarted({ id, status }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    ticketsApi.util.updateQueryData('getTicket', id, (draft) => {
                        draft.status = status;
                        // Mirror isOverdue()'s live rule so the "Depasit" badge
                        // clears/sets immediately instead of after the refetch.
                        draft.overdue =
                            status !== 'done' && draft.dueDate
                                ? new Date(draft.dueDate).getTime() < Date.now()
                                : false;
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        // Drag-reorder: drop the ticket between two visible neighbours. `prevId`
        // is the card that ends up just above it, `nextId` just below; either is
        // null at the top/bottom. The UI applies the move optimistically (local
        // order state in the list/board), so no onQueryStarted patch here — the
        // LIST invalidation just reconciles against the server's chosen position.
        reorderTicket: builder.mutation<
            Ticket,
            { id: number; prevId: number | null; nextId: number | null }
        >({
            query: ({ id, prevId, nextId }) => ({
                url: '/v2/tickets/[id]/reorder',
                method: 'POST',
                query: { id },
                body: { prevId, nextId },
            }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        assignTicket: builder.mutation<
            Ticket,
            { id: number; assigneeUserId: number | null; assigneeName: string | null }
        >({
            query: ({ id, assigneeUserId, assigneeName }) => ({
                url: '/v2/tickets/[id]/assign',
                method: 'POST',
                query: { id },
                body: { assigneeUserId, assigneeName },
            }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
                { type: 'TicketEvent', id },
            ],
            async onQueryStarted({ id, assigneeUserId, assigneeName }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    ticketsApi.util.updateQueryData('getTicket', id, (draft) => {
                        draft.assigneeUserId = assigneeUserId;
                        draft.assigneeName = assigneeName;
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        deleteTicket: builder.mutation<void, number>({
            query: (id) => ({ url: '/v2/tickets/[id]', method: 'DELETE', query: { id } }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        archiveTicket: builder.mutation<Ticket, number>({
            query: (id) => ({ url: '/v2/tickets/[id]/archive', method: 'POST', query: { id }, body: {} }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, id) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        unarchiveTicket: builder.mutation<Ticket, number>({
            query: (id) => ({ url: '/v2/tickets/[id]/unarchive', method: 'POST', query: { id }, body: {} }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, id) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        // Send a ticket to the backlog (off the active board). Reversible from the
        // backlog page; the LIST invalidation drops it from the board on refetch.
        backlogTicket: builder.mutation<Ticket, number>({
            query: (id) => ({ url: '/v2/tickets/[id]/backlog', method: 'POST', query: { id }, body: {} }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, id) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        // Pull a ticket out of the backlog back onto the active board.
        restoreTicket: builder.mutation<Ticket, number>({
            query: (id) => ({ url: '/v2/tickets/[id]/unbacklog', method: 'POST', query: { id }, body: {} }),
            transformResponse: (raw: unknown) => TicketResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, id) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
        }),

        archiveAllDone: builder.mutation<{ archived: number }, void>({
            query: () => ({ url: '/v2/tickets/archive-done', method: 'POST', body: {} }),
            transformResponse: (raw: unknown) => ArchiveAllResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'Ticket', id: 'LIST' }],
        }),

        listTicketEvents: builder.query<TicketEvent[], number>({
            query: (id) => ({ url: '/v2/tickets/[id]/events', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => TicketEventListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'TicketEvent', id }],
        }),

        addComment: builder.mutation<TicketEvent, { id: number; values: CommentForm }>({
            query: ({ id, values }) => ({
                url: '/v2/tickets/[id]/comments',
                method: 'POST',
                query: { id },
                body: { body: values.body },
            }),
            transformResponse: (raw: unknown) => TicketEventSingleResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'TicketEvent', id },
                { type: 'Ticket', id },
            ],
            async onQueryStarted({ id, values }, { dispatch, getState, queryFulfilled }) {
                const auth = (getState() as { authentication?: { user?: { id?: number; name?: string } } })
                    .authentication;
                const user = auth?.user;
                const patch = dispatch(
                    ticketsApi.util.updateQueryData('getTicket', id, (draft) => {
                        if (!draft.events) draft.events = [];
                        // Newest-first, matching the server order the timeline assumes.
                        draft.events.unshift({
                            id: nextOptimisticId(),
                            ticketId: id,
                            type: 'comment',
                            body: values.body,
                            authorUserId: user?.id ?? null,
                            authorName: user?.name ?? null,
                            createdAt: new Date().toISOString(),
                        });
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        listAttachments: builder.query<Attachment[], number>({
            query: (id) => ({ url: '/v2/tickets/[id]/attachments', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => AttachmentListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'TicketAttachment', id }],
        }),

        // Base64 JSON upload — the admin HTTP wrapper forces application/json, so
        // multipart never reaches the API; we send the dataURL in the body.
        addAttachment: builder.mutation<
            Attachment,
            { id: number; filename: string; contentType: string; data: string }
        >({
            query: ({ id, filename, contentType, data }) => ({
                url: '/v2/tickets/[id]/attachments',
                method: 'POST',
                query: { id },
                body: { filename, contentType, data },
            }),
            transformResponse: (raw: unknown) => AttachmentSingleResponseSchema.parse(raw).data,
            // Also Ticket:{id} so the activity timeline (on getTicket) picks up
            // the attachment history event.
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'TicketAttachment', id },
                { type: 'Ticket', id },
            ],
        }),

        deleteAttachment: builder.mutation<void, { id: number; storageId: number }>({
            query: ({ id, storageId }) => ({
                url: '/v2/tickets/[id]/attachments/[storageId]',
                method: 'DELETE',
                query: { id, storageId },
            }),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'TicketAttachment', id },
                { type: 'Ticket', id },
            ],
        }),

        getDashboard: builder.query<Dashboard, void>({
            query: () => ({ url: '/v2/tickets/dashboard', method: 'GET' }),
            transformResponse: (raw: unknown) => DashboardResponseSchema.parse(raw).data,
            providesTags: [{ type: 'Ticket', id: 'LIST' }],
        }),

        listReferences: builder.query<TicketReference[], number>({
            query: (id) => ({ url: '/v2/tickets/[id]/references', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => TicketReferenceListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'TicketReference', id }],
        }),

        addReference: builder.mutation<
            TicketReference,
            { id: number; itemType: string; itemId: string; label?: string }
        >({
            query: ({ id, itemType, itemId, label }) => ({
                url: '/v2/tickets/[id]/references',
                method: 'POST',
                query: { id },
                body: { itemType, itemId, label },
            }),
            transformResponse: (raw: unknown) => TicketReferenceSingleResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'TicketReference', id },
                { type: 'Ticket', id },
            ],
        }),

        deleteReference: builder.mutation<void, { id: number; refId: number }>({
            query: ({ id, refId }) => ({
                url: '/v2/tickets/[id]/references/[refId]',
                method: 'DELETE',
                query: { id, refId },
            }),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'TicketReference', id },
                { type: 'Ticket', id },
            ],
        }),

        addLabel: builder.mutation<TicketLabel, { id: number; label: string }>({
            query: ({ id, label }) => ({
                url: '/v2/tickets/[id]/labels',
                method: 'POST',
                query: { id },
                body: { label },
            }),
            transformResponse: (raw: unknown) => TicketLabelResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
            async onQueryStarted({ id, label }, { dispatch, queryFulfilled }) {
                const name = label.trim();
                const patch = dispatch(
                    ticketsApi.util.updateQueryData('getTicket', id, (draft) => {
                        if (!draft.labels) draft.labels = [];
                        // Skip the optimistic add if it's already there (the backend
                        // is idempotent and dedupes case-insensitively too).
                        if (!draft.labels.some((l) => l.label.toLowerCase() === name.toLowerCase())) {
                            draft.labels.push({ id: nextOptimisticId(), label: name });
                        }
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        removeLabel: builder.mutation<void, { id: number; labelId: number }>({
            query: ({ id, labelId }) => ({
                url: '/v2/tickets/[id]/labels/[labelId]',
                method: 'DELETE',
                query: { id, labelId },
            }),
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Ticket', id },
                { type: 'Ticket', id: 'LIST' },
            ],
            async onQueryStarted({ id, labelId }, { dispatch, queryFulfilled }) {
                const patch = dispatch(
                    ticketsApi.util.updateQueryData('getTicket', id, (draft) => {
                        if (draft.labels) draft.labels = draft.labels.filter((l) => l.id !== labelId);
                    }),
                );
                try {
                    await queryFulfilled;
                } catch {
                    patch.undo();
                }
            },
        }),

        // Autocomplete source — distinct labels across all tickets. Ephemeral
        // lookup, no cache tags.
        suggestLabels: builder.query<string[], string | void>({
            query: (q) => ({ url: '/v2/tickets/labels/suggest', method: 'GET', params: { q: q || undefined } }),
            transformResponse: (raw: unknown) => LabelSuggestResponseSchema.parse(raw).data,
        }),

        // Global label -> colour map (lowercased label keys). Shared by every chip.
        getLabelColors: builder.query<Record<string, string>, void>({
            query: () => ({ url: '/v2/tickets/labels/colors', method: 'GET' }),
            transformResponse: (raw: unknown) => LabelColorMapResponseSchema.parse(raw).data,
            providesTags: [{ type: 'TicketLabelColor', id: 'MAP' }],
        }),

        setLabelColor: builder.mutation<{ label: string; color: string }, { label: string; color: string }>({
            query: ({ label, color }) => ({
                url: '/v2/tickets/labels/color',
                method: 'PUT',
                body: { label, color },
            }),
            transformResponse: (raw: unknown) => LabelColorResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'TicketLabelColor', id: 'MAP' }],
        }),

        // Recurring-ticket templates (chores). Manager/Admin-only server-side;
        // the UI additionally hides the page from Staff/Shop.
        listRecurrences: builder.query<TicketRecurrence[], void>({
            query: () => ({ url: '/v2/tickets/recurrences', method: 'GET' }),
            transformResponse: (raw: unknown) => TicketRecurrenceListResponseSchema.parse(raw).data,
            providesTags: [{ type: 'TicketRecurrence', id: 'LIST' }],
        }),

        createRecurrence: builder.mutation<TicketRecurrence, Record<string, unknown>>({
            query: (body) => ({ url: '/v2/tickets/recurrences', method: 'POST', body }),
            transformResponse: (raw: unknown) => TicketRecurrenceResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'TicketRecurrence', id: 'LIST' }],
        }),

        updateRecurrence: builder.mutation<TicketRecurrence, { id: number; values: Record<string, unknown> }>({
            query: ({ id, values }) => ({
                url: '/v2/tickets/recurrences/[id]',
                method: 'PUT',
                query: { id },
                body: values,
            }),
            transformResponse: (raw: unknown) => TicketRecurrenceResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'TicketRecurrence', id: 'LIST' }],
        }),

        deleteRecurrence: builder.mutation<void, number>({
            query: (id) => ({ url: '/v2/tickets/recurrences/[id]', method: 'DELETE', query: { id } }),
            invalidatesTags: [{ type: 'TicketRecurrence', id: 'LIST' }],
        }),

        // Order search for the reference picker — reuses the legacy /orders list
        // (q matches order number / customer / phone / email). No caching tags;
        // this is an ephemeral lookup, not owned ticket data.
        searchOrders: builder.query<OrderSearchItem[], string | void>({
            query: (q) => ({
                url: '/orders',
                method: 'GET',
                params: { _start: 0, _end: 15, _sort: 'id', _order: 'DESC', q: q || undefined },
            }),
            transformResponse: (raw: unknown) => OrderSearchResponseSchema.parse(raw).data,
        }),
    }),
});

export const {
    useListTicketsQuery,
    useGetTicketQuery,
    useCreateTicketMutation,
    useUpdateTicketMutation,
    useSetTicketStatusMutation,
    useReorderTicketMutation,
    useAssignTicketMutation,
    useDeleteTicketMutation,
    useArchiveTicketMutation,
    useUnarchiveTicketMutation,
    useBacklogTicketMutation,
    useRestoreTicketMutation,
    useArchiveAllDoneMutation,
    useListTicketEventsQuery,
    useAddCommentMutation,
    useListAttachmentsQuery,
    useAddAttachmentMutation,
    useDeleteAttachmentMutation,
    useGetDashboardQuery,
    useListReferencesQuery,
    useAddReferenceMutation,
    useDeleteReferenceMutation,
    useAddLabelMutation,
    useRemoveLabelMutation,
    useSuggestLabelsQuery,
    useGetLabelColorsQuery,
    useSetLabelColorMutation,
    useListRecurrencesQuery,
    useCreateRecurrenceMutation,
    useUpdateRecurrenceMutation,
    useDeleteRecurrenceMutation,
    useSearchOrdersQuery,
} = ticketsApi;
